import * as React from 'react';
import {
    Checkbox,
    CommandBar,
    DefaultButton,
    DetailsList,
    DetailsListLayoutMode,
    Dialog,
    DialogFooter,
    DialogType,
    Dropdown,
    IDropdownOption,
    IColumn,
    ICommandBarItemProps,
    IconButton,
    Label,
    MessageBar,
    MessageBarType,
    Panel,
    PanelType,
    PrimaryButton,
    Selection,
    SelectionMode,
    Separator,
    Spinner,
    SpinnerSize,
    Stack,
    Text,
    TextField,
} from '@fluentui/react';
import { ApiHelper } from '../../../ApiHelper';
import { Environment, Process, Service } from '../../../interfaces/Service';
import {
    AgentTaskListDetailResult,
    buildDefaultTaskListDisplayName,
    CreateTaskListFormState,
    DEFAULT_TASK_LIST_GRANULARITY_PERCENT,
    CREATABLE_TASK_TYPES,
    TaskListCreateConflictResponse,
    getDerivedMetricId,
    getTaskTypeLabel,
    isProfileBackedTaskType,
    isUnsupportedTaskType,
    isUploadBackedTaskType,
    submitMultipartRequest,
    useProfileDates,
} from '../shared';

export function CreateTaskListPanel(props: {
    isOpen: boolean;
    serviceName: string;
    service?: Service;
    onDismiss: () => void;
    onCreated: (result: AgentTaskListDetailResult) => void;
}) {
    const { isOpen, serviceName, service, onDismiss, onCreated } = props;
    const [formState, setFormState] = React.useState<CreateTaskListFormState>({
        name: '',
        processId: service?.defaultConfig?.processId ?? service?.Processes?.[0]?.id,
        taskType: 0,
        granularityPercent: DEFAULT_TASK_LIST_GRANULARITY_PERCENT,
        environmentId: service?.defaultConfig?.environmentId ?? service?.Environments?.[0]?.id,
        date: undefined,
        file: null,
    });
    const [submitting, setSubmitting] = React.useState(false);
    const [submitError, setSubmitError] = React.useState<string | null>(null);
    const [duplicateConfirmation, setDuplicateConfirmation] = React.useState<{ matchingCount: number; latestGeneration: number } | null>(null);

    React.useEffect(() => {
        if (!isOpen) {
            return;
        }

        setFormState({
            name: '',
            processId: service?.defaultConfig?.processId ?? service?.Processes?.[0]?.id,
            taskType: 0,
            granularityPercent: DEFAULT_TASK_LIST_GRANULARITY_PERCENT,
            environmentId: service?.defaultConfig?.environmentId ?? service?.Environments?.[0]?.id,
            date: undefined,
            file: null,
        });
        setSubmitError(null);
        setDuplicateConfirmation(null);
    }, [isOpen, service]);

    const selectedProcess = React.useMemo(
        () => service?.Processes?.find((process: Process) => process.id === formState.processId),
        [formState.processId, service],
    );
    const defaultDisplayName = React.useMemo(
        () => buildDefaultTaskListDisplayName(
            service?.name ?? serviceName,
            selectedProcess?.friendlyName ?? selectedProcess?.name ?? '',
            getTaskTypeLabel(formState.taskType),
        ),
        [formState.taskType, selectedProcess?.friendlyName, selectedProcess?.name, service?.name, serviceName],
    );

    const selectedMetricId = React.useMemo(
        () => getDerivedMetricId(selectedProcess, formState.taskType),
        [formState.taskType, selectedProcess],
    );

    const { dates, loading: loadingDates, error: dateError } = useProfileDates(
        serviceName,
        isProfileBackedTaskType(formState.taskType),
        formState.environmentId,
        formState.processId,
        selectedMetricId,
    );

    React.useEffect(() => {
        if (!isProfileBackedTaskType(formState.taskType)) {
            return;
        }

        if (dates.length > 0 && (!formState.date || !dates.some(date => date.dateAsString === formState.date))) {
            setFormState(current => ({
                ...current,
                date: dates[0].dateAsString,
            }));
        }
    }, [dates, formState.date, formState.taskType]);

    async function handleSubmit(forceCreateFresh = false) {
        setSubmitting(true);
        setSubmitError(null);

        try {
            if (isUnsupportedTaskType(formState.taskType)) {
                throw new Error('Memory monitored scopes is not implemented yet.');
            }

            if (formState.processId === undefined) {
                throw new Error('Process is required.');
            }

            const granularityPercent = Number.parseFloat(formState.granularityPercent);
            if (!Number.isFinite(granularityPercent) || granularityPercent <= 0 || granularityPercent >= 100) {
                throw new Error('Granularity must be greater than 0% and less than 100%.');
            }

            const granularity = granularityPercent / 100;
            const requestedDisplayName = formState.name.trim() || defaultDisplayName;

            if (isProfileBackedTaskType(formState.taskType)) {
                if (formState.environmentId === undefined || !formState.date) {
                    throw new Error('Environment and profile date are required.');
                }

                if (selectedMetricId === undefined) {
                    throw new Error('The selected process does not define the required profile metric for this task type.');
                }

                const response = await ApiHelper(`/api/service/${serviceName}/agenttasklists`, 'POST', {
                    name: requestedDisplayName,
                    processId: formState.processId,
                    taskType: formState.taskType,
                    granularity,
                    forceCreateFresh,
                    sourceKind: 0,
                    environmentId: formState.environmentId,
                    metricId: selectedMetricId,
                    date: formState.date,
                }, true) as AgentTaskListDetailResult | { data: TaskListCreateConflictResponse | null; status: number; error: true };

                if ('error' in response && response.error) {
                    if (response.status === 409) {
                        const conflict = response.data as TaskListCreateConflictResponse | null;
                        setDuplicateConfirmation({
                            matchingCount: conflict?.matchingCount ?? 1,
                            latestGeneration: conflict?.latestGeneration ?? 1,
                        });
                        return;
                    }

                    throw new Error((response.data as { message?: string } | null)?.message ?? 'Failed to create the task list.');
                }

                const result = response as AgentTaskListDetailResult;

                onCreated(result);
                return;
            }

            if (isUploadBackedTaskType(formState.taskType)) {
                if (!formState.file) {
                    throw new Error('Please select the master JSON file to upload.');
                }

                const response = await submitMultipartRequest<AgentTaskListDetailResult>(
                    `/api/service/${serviceName}/agenttasklists`,
                    {
                        name: requestedDisplayName,
                        processId: formState.processId,
                        taskType: formState.taskType,
                        granularity,
                        forceCreateFresh: forceCreateFresh ? 1 : undefined,
                        sourceKind: 1,
                    },
                    formState.file,
                    true,
                ) as AgentTaskListDetailResult | { data: TaskListCreateConflictResponse | null; status: number; error: true };

                if ('error' in response && response.error) {
                    if (response.status === 409) {
                        const conflict = response.data as TaskListCreateConflictResponse | null;
                        setDuplicateConfirmation({
                            matchingCount: conflict?.matchingCount ?? 1,
                            latestGeneration: conflict?.latestGeneration ?? 1,
                        });
                        return;
                    }

                    throw new Error((response.data as { message?: string } | null)?.message ?? 'Failed to create the task list.');
                }

                const result = response as AgentTaskListDetailResult;

                onCreated(result);
                return;
            }
        } catch (err) {
            setSubmitError(err instanceof Error ? err.message : 'Failed to create the task list.');
        } finally {
            setSubmitting(false);
        }
    }

    return (
        <Panel
            isOpen={isOpen}
            onDismiss={onDismiss}
            type={PanelType.medium}
            headerText="Create agent task list"
        >
            <Dialog
                hidden={!duplicateConfirmation}
                onDismiss={() => !submitting && setDuplicateConfirmation(null)}
                dialogContentProps={{
                    type: DialogType.normal,
                    title: 'Create fresh task list?',
                    subText: duplicateConfirmation
                        ? `Found ${duplicateConfirmation.matchingCount} existing task list${duplicateConfirmation.matchingCount === 1 ? '' : 's'} for ${selectedProcess?.friendlyName ?? 'the selected process'} / ${getTaskTypeLabel(formState.taskType)}. The latest is generation ${duplicateConfirmation.latestGeneration}. Create a fresh task list anyway? The backend will assign the next generation automatically.`
                        : undefined,
                }}
                modalProps={{
                    isBlocking: true,
                }}
            >
                <DialogFooter>
                    <PrimaryButton
                        text={submitting ? 'Creating...' : 'Create fresh list'}
                        onClick={() => {
                            setDuplicateConfirmation(null);
                            void handleSubmit(true);
                        }}
                        disabled={submitting}
                    />
                    <DefaultButton
                        text="Cancel"
                        onClick={() => setDuplicateConfirmation(null)}
                        disabled={submitting}
                    />
                </DialogFooter>
            </Dialog>

            <Stack tokens={{ childrenGap: 16 }}>
                {submitError && (
                    <MessageBar messageBarType={MessageBarType.error}>
                        {submitError}
                    </MessageBar>
                )}

                <TextField
                    label="Display name"
                    value={formState.name}
                    onChange={(_, value) => setFormState(current => ({ ...current, name: value ?? '' }))}
                    placeholder={defaultDisplayName}
                    description={`Defaults to "${defaultDisplayName}" when left blank.`}
                />

                <Dropdown
                    label="Task type"
                    options={CREATABLE_TASK_TYPES}
                    selectedKey={formState.taskType}
                    onChange={(_, option) => {
                        const taskType = Number(option?.key ?? 0);
                        setFormState(current => ({
                            ...current,
                            taskType,
                            date: undefined,
                            file: null,
                        }));
                    }}
                />

                <Dropdown
                    label="Process"
                    options={(service?.Processes ?? []).map((process: Process) => ({ key: process.id, text: process.friendlyName }))}
                    selectedKey={formState.processId}
                    onChange={(_, option) => setFormState(current => ({ ...current, processId: Number(option?.key) }))}
                />

                <TextField
                    label="Granularity (%)"
                    type="number"
                    value={formState.granularityPercent}
                    onChange={(_, value) => setFormState(current => ({
                        ...current,
                        granularityPercent: value ?? DEFAULT_TASK_LIST_GRANULARITY_PERCENT,
                    }))}
                    description="Minimum share of total cost required before a scope becomes its own task."
                />

                {isUnsupportedTaskType(formState.taskType) && (
                    <MessageBar messageBarType={MessageBarType.warning}>
                        Memory monitored scopes is intentionally blocked until that source flow is implemented.
                    </MessageBar>
                )}

                {isProfileBackedTaskType(formState.taskType) && (
                    <>
                        <Dropdown
                            label="Environment"
                            options={(service?.Environments ?? []).map((environment: Environment) => ({ key: environment.id, text: environment.name }))}
                            selectedKey={formState.environmentId}
                            onChange={(_, option) => setFormState(current => ({ ...current, environmentId: Number(option?.key) }))}
                        />

                        {selectedMetricId === undefined && (
                            <MessageBar messageBarType={MessageBarType.error}>
                                The selected process does not define the required profile metric for this task type.
                            </MessageBar>
                        )}

                        {dateError && (
                            <MessageBar messageBarType={MessageBarType.error}>{dateError}</MessageBar>
                        )}

                        <Dropdown
                            label="Profile date"
                            options={dates.map(date => ({ key: date.dateAsString, text: date.dateAsString }))}
                            selectedKey={formState.date}
                            onChange={(_, option) => setFormState(current => ({ ...current, date: String(option?.key) }))}
                            disabled={loadingDates || dates.length === 0}
                            placeholder={loadingDates ? 'Loading available profiles...' : 'Select a profile date'}
                        />

                        {dates.length === 0 && !loadingDates && (
                            <MessageBar messageBarType={MessageBarType.info}>
                                No available untrimmed profiles were found for the selected environment and process.
                            </MessageBar>
                        )}
                    </>
                )}

                {isUploadBackedTaskType(formState.taskType) && (
                    <>
                        <Label>Master JSON file</Label>
                        <input
                            type="file"
                            accept=".json,application/json"
                            onChange={event => setFormState(current => ({
                                ...current,
                                file: event.target.files?.[0] ?? null,
                            }))}
                        />
                        <Text variant="small" styles={{ root: { color: '#605e5c' } }}>
                            The initial implementation accepts the file in one API call and uses the synthetic test-mode backend to unblock UX work.
                        </Text>
                    </>
                )}

                <Stack horizontal tokens={{ childrenGap: 8 }}>
                    <PrimaryButton
                        text={submitting ? 'Creating...' : 'Create task list'}
                        onClick={() => {
                            void handleSubmit();
                        }}
                        disabled={submitting || isUnsupportedTaskType(formState.taskType)}
                    />
                    <DefaultButton text="Cancel" onClick={onDismiss} disabled={submitting} />
                </Stack>
            </Stack>
        </Panel>
    );
}

